/**
 * Unidades del Portal Condominio tal como las arma el servidor (`get_condominio_unidades`) y los
 * cálculos que se derivan de ellas. Sin dependencias de Supabase: se prueban solas.
 *
 * Reglas (Eduardo, 2026-10-15):
 *   - Cobrado = aplicaciones activas sin multas sobre acuerdos activos. Lo pagado que no está
 *     aplicado a un acuerdo activo va aparte en `por_aplicar`.
 *   - Cartera = mantenimiento + fondo de reserva, desglosados. Las multas van aparte.
 *   - Los montos se suman en centavos enteros: un float suelto deja $0.01 fantasma.
 */
import { formatCuentaMantenimientoId } from "@/utils/cuentaCobranzaUtils";

export interface UnidadCondominioRpc {
  id_cuenta: number;
  id_cuenta_padre: number;
  id_propiedad: number;
  unidad: string;
  piso: string | null;
  tipo: string | null;
  edificio: string | null;
  modelo: string | null;
  area_m2: number;
  /** Comprador titular de la venta (no la empresa dueña de la unidad). */
  propietario: string | null;
  /** Los otros compradores, separados por coma. */
  copropietarios: string | null;
  clabe: string | null;
  cuota_mensual: number;
  mtto_devengado: number;
  mtto_aplicado: number;
  mtto_vencido: number;
  mtto_pendiente: number;
  fondo_devengado: number;
  fondo_aplicado: number;
  fondo_vencido: number;
  fondo_pendiente: number;
  multas_pendiente: number;
  pagado: number;
  por_aplicar: number;
  /** Días desde la cuota vencida más antigua sin cubrir; 0 = al corriente. */
  dias_vencido: number;
  ultimo_pago: string | null;
  proxima_fecha: string | null;
  /** Monto del pago activo más reciente; `null` sin pagos o antes de la migración 20261015220000. */
  ultimo_pago_monto: number | null;
  /** Evidencias de ese pago: CEP verificado y comprobante cargado (ver `lib/evidenciasPago`). */
  ultimo_pago_url_cep: string | null;
  ultimo_pago_url_recibo: string | null;
}

export interface TendenciaCondominio {
  mes: string;
  mtto_esperado: number;
  fondo_esperado: number;
  cobrado: number;
}

const NUMERICOS: (keyof UnidadCondominioRpc)[] = [
  "id_cuenta", "id_cuenta_padre", "id_propiedad", "area_m2", "cuota_mensual",
  "mtto_devengado", "mtto_aplicado", "mtto_vencido", "mtto_pendiente",
  "fondo_devengado", "fondo_aplicado", "fondo_vencido", "fondo_pendiente",
  "multas_pendiente", "pagado", "por_aplicar", "dias_vencido",
];

/** PostgREST devuelve `numeric` como texto: se convierte una vez al llegar. */
export function normalizarUnidad(fila: Record<string, unknown>): UnidadCondominioRpc {
  const u = { ...fila } as Record<string, unknown>;
  for (const k of NUMERICOS) u[k] = Number(u[k] ?? 0) || 0;
  u.ultimo_pago_monto = u.ultimo_pago_monto == null ? null : Number(u.ultimo_pago_monto) || 0;
  u.ultimo_pago_url_cep ??= null;
  u.ultimo_pago_url_recibo ??= null;
  return u as unknown as UnidadCondominioRpc;
}

export const centavos = (n: number): number => Math.round((n || 0) * 100);
const sumar = (filas: UnidadCondominioRpc[], f: (u: UnidadCondominioRpc) => number): number =>
  filas.reduce((s, u) => s + centavos(f(u)), 0) / 100;

export const folioMantenimiento = (u: Pick<UnidadCondominioRpc, "id_cuenta">): string =>
  formatCuentaMantenimientoId(u.id_cuenta);

/** Vencido de la cartera (mantenimiento + fondo de reserva). */
export const vencidoDe = (u: UnidadCondominioRpc): number =>
  (centavos(u.mtto_vencido) + centavos(u.fondo_vencido)) / 100;

/** Por cobrar de la cartera, vencido o no (mantenimiento + fondo de reserva). */
export const pendienteDe = (u: UnidadCondominioRpc): number =>
  (centavos(u.mtto_pendiente) + centavos(u.fondo_pendiente)) / 100;

/** Cobrado de la cartera (aplicado a mantenimiento + fondo de reserva). */
export const aplicadoDe = (u: UnidadCondominioRpc): number =>
  (centavos(u.mtto_aplicado) + centavos(u.fondo_aplicado)) / 100;

/** Con adeudo: algo vencido, en centavos (tolerancia 0). */
export const esMoroso = (u: UnidadCondominioRpc): boolean => centavos(u.mtto_vencido) + centavos(u.fondo_vencido) > 0;

export interface KpisCondominio {
  unidades: number;
  morosos: number;
  vencido: number;
  vencidoMtto: number;
  vencidoFondo: number;
  pendiente: number;
  aplicado: number;
  devengado: number;
  /** Cobrado / devengado a la fecha, en %. null sin devengado. */
  avance: number | null;
  porAplicar: number;
  unidadesPorAplicar: number;
  multas: number;
}

export function kpisCondominio(filas: UnidadCondominioRpc[]): KpisCondominio {
  const devengado = sumar(filas, (u) => u.mtto_devengado + u.fondo_devengado);
  const aplicado = sumar(filas, aplicadoDe);
  return {
    unidades: filas.length,
    morosos: filas.filter(esMoroso).length,
    vencido: sumar(filas, vencidoDe),
    vencidoMtto: sumar(filas, (u) => u.mtto_vencido),
    vencidoFondo: sumar(filas, (u) => u.fondo_vencido),
    pendiente: sumar(filas, pendienteDe),
    aplicado,
    devengado,
    avance: devengado > 0 ? Math.round((aplicado / devengado) * 1000) / 10 : null,
    porAplicar: sumar(filas, (u) => Math.max(u.por_aplicar, 0)),
    unidadesPorAplicar: filas.filter((u) => centavos(u.por_aplicar) > 0).length,
    multas: sumar(filas, (u) => u.multas_pendiente),
  };
}

export const RANGOS_ANTIGUEDAD = ["1-30", "31-60", "61-90", "90+"] as const;
export type RangoAntiguedad = (typeof RANGOS_ANTIGUEDAD)[number];

export function rangoAntiguedad(dias: number): RangoAntiguedad | null {
  if (dias <= 0) return null;
  if (dias <= 30) return "1-30";
  if (dias <= 60) return "31-60";
  if (dias <= 90) return "61-90";
  return "90+";
}

/** Cartera vencida por antigüedad de la cuota más vieja sin cubrir de cada unidad. */
export function antiguedadCartera(filas: UnidadCondominioRpc[]): { rango: string; monto: number; unidades: number }[] {
  return RANGOS_ANTIGUEDAD.map((rango) => {
    const del = filas.filter((u) => esMoroso(u) && rangoAntiguedad(u.dias_vencido) === rango);
    return { rango: `${rango} días`, monto: sumar(del, vencidoDe), unidades: del.length };
  });
}

/** Unidades con más adeudo vencido, de mayor a menor. */
export function topMorosos(filas: UnidadCondominioRpc[], n = 8): UnidadCondominioRpc[] {
  return filas.filter(esMoroso).sort((a, b) => vencidoDe(b) - vencidoDe(a)).slice(0, n);
}

export type EstadoUnidad = "" | "con_adeudo" | "al_corriente" | "por_aplicar";

// ─── Búsqueda de Departamentos ───────────────────────────────────────────────
//
// Mismo buscador que Cuentas de cobranza (`ui/busqueda-inteligente`): "Automático" decide el campo por
// la forma del texto y busca dinámico (lo que empieza o contiene); elegir un campo lo fija y en
// unidad, folio y CLABE busca exacto. El propietario siempre busca por contenido (incluye a los
// copropietarios) porque un nombre exacto casi nunca se escribe completo.

export type CampoBusquedaUnidad = "unidad" | "folio" | "propietario" | "clabe";
export type ModoBusquedaUnidad = "auto" | CampoBusquedaUnidad;

export const CAMPOS_BUSQUEDA_UNIDAD: CampoBusquedaUnidad[] = ["unidad", "folio", "propietario", "clabe"];
export const ETIQUETA_CAMPO_UNIDAD: Record<CampoBusquedaUnidad, string> = {
  unidad: "Unidad",
  folio: "Folio",
  propietario: "Propietario",
  clabe: "CLABE",
};

export const esModoBusquedaUnidad = (v: string): v is ModoBusquedaUnidad =>
  v === "auto" || (CAMPOS_BUSQUEDA_UNIDAD as string[]).includes(v);

const soloDigitos = (v: string): string => v.replace(/\D/g, "");
const plano = (v: string | null | undefined): string =>
  (v ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

/**
 * Campo al que va el texto en modo Automático: `CM-…` o 6+ dígitos es folio (CM-001530), 10+
 * dígitos es CLABE, un número corto (con letra opcional, "101A") es unidad y lo demás, propietario.
 */
export function campoBusquedaUnidad(texto: string): CampoBusquedaUnidad | null {
  const t = texto.trim();
  if (!t) return null;
  if (/^cm[-\s]?\d*$/i.test(t)) return "folio";
  const digitos = soloDigitos(t);
  if (/^[\d\s-]+$/.test(t)) return digitos.length >= 10 ? "clabe" : digitos.length >= 6 ? "folio" : "unidad";
  if (/^\d+[a-z]?$/i.test(t)) return "unidad";
  return "propietario";
}

function coincide(u: UnidadCondominioRpc, campo: CampoBusquedaUnidad, texto: string, exacto: boolean): boolean {
  switch (campo) {
    case "unidad": {
      const q = plano(texto);
      const v = plano(u.unidad);
      return exacto ? v === q : v.startsWith(q);
    }
    case "folio": {
      const q = soloDigitos(texto);
      if (!q) return /^cm[-\s]?$/i.test(texto.trim());
      return exacto ? Number(q) === u.id_cuenta : soloDigitos(folioMantenimiento(u)).includes(q);
    }
    case "clabe": {
      const q = soloDigitos(texto);
      const v = soloDigitos(u.clabe ?? "");
      return exacto ? v === q : v.includes(q);
    }
    case "propietario": {
      const q = plano(texto);
      return plano(u.propietario).includes(q) || plano(u.copropietarios).includes(q);
    }
  }
}

/** Filtro de Departamentos: búsqueda (ver arriba), edificio y estado. */
export function filtrarUnidades(
  filas: UnidadCondominioRpc[],
  f: { buscar: string; buscarEn?: ModoBusquedaUnidad; edificio: string; estado: EstadoUnidad },
): UnidadCondominioRpc[] {
  const texto = f.buscar.trim();
  const modo = f.buscarEn ?? "auto";
  const campo = modo === "auto" ? campoBusquedaUnidad(texto) : modo;
  return filas.filter((u) => {
    if (f.edificio && u.edificio !== f.edificio) return false;
    if (f.estado === "con_adeudo" && !esMoroso(u)) return false;
    if (f.estado === "al_corriente" && esMoroso(u)) return false;
    if (f.estado === "por_aplicar" && centavos(u.por_aplicar) <= 0) return false;
    if (!texto || !campo) return true;
    return coincide(u, campo, texto, modo !== "auto");
  });
}
